/**
 * How a shared link left the device, or why it didn't.
 *
 * - `shared` — the share sheet opened and the user picked somewhere to send it.
 * - `copied` — the message is on the clipboard; the caller says so.
 * - `dismissed` — the user closed the share sheet. Not a failure, and nothing
 *   to say about it.
 * - `blocked` — the browser refused both. Usually because the tap that asked
 *   is too long ago: browsers only share or write to the clipboard close to a
 *   tap, and a request ran first. The caller offers a fresh tap.
 */
export type ShareOutcome = "shared" | "copied" | "dismissed" | "blocked";

interface ShareLinkOptions {
  url: string;
  /** The line above the link, e.g. "Issue 024 · Fire-stopping missing". */
  text: string;
  /**
   * Open the share sheet rather than copy. True on a touch device: a phone's
   * share sheet goes straight into WhatsApp, while on a desktop the user wants
   * something to paste.
   */
  viaShareSheet: boolean;
}

/** The message as sent or copied: the text, then the link on its own line. */
export function shareMessage(text: string, url: string): string {
  return `${text}\n${url}`;
}

function errorName(error: unknown): string | undefined {
  return typeof error === "object" && error !== null && "name" in error
    ? String((error as { name: unknown }).name)
    : undefined;
}

/**
 * Hand a link to the phone's share sheet, or copy it.
 *
 * The link rides inside `text` rather than in `url`, so whatever the user
 * shares to receives the issue line and the link as one message; share
 * targets differ in which of the two fields they keep when given both.
 *
 * When the share sheet fails for any reason but a dismissal, the clipboard is
 * tried before giving up. Never throws.
 */
export async function shareLink({
  url,
  text,
  viaShareSheet,
}: ShareLinkOptions): Promise<ShareOutcome> {
  const message = shareMessage(text, url);

  if (viaShareSheet && typeof navigator.share === "function") {
    try {
      await navigator.share({ text: message });
      return "shared";
    } catch (error) {
      if (errorName(error) === "AbortError") return "dismissed";
    }
  }

  if (typeof navigator.clipboard?.writeText === "function") {
    try {
      await navigator.clipboard.writeText(message);
      return "copied";
    } catch {
      return "blocked";
    }
  }

  return "blocked";
}
